import React, { forwardRef } from 'react';
import { useForm, useWatch, useFormState } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';

import PhoneInput from 'components/PhoneInput';
import FormInput from 'components/FormInput';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';

import { txtpayShare } from 'redux/slices/fundraiser';
import schema, { FIELD_NAMES, defaultValues } from './schema';
import { currentStoreSelector } from 'redux/slices/javelinStore';

import { txtPay as TEXT } from 'locales/en';

const TxtpayForm = forwardRef(
  ({ id, fundraiserName, checkoutUrl, className }, ref) => {
    const dispatch = useDispatch();
    const store = useSelector(currentStoreSelector);
    const { register, handleSubmit, control, clearErrors } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        ...defaultValues,
        [FIELD_NAMES.sms_content]: `"${store.name}" has requested a donation. Tap the link to Donate Now! ${checkoutUrl}`,
        [FIELD_NAMES.email_content]: `It would be great if you could make a donation to "${fundraiserName}" so we can reach the goal.\n\nJust use the link below.\n\n${checkoutUrl}\n\nAny donation big or small is greatly appreciated.\n\nThanks for your support!`,
      },
    });
    const { errors, isSubmitting } = useFormState({
      control,
    });
    const channel = useWatch({
      control,
      name: FIELD_NAMES.channel,
    });

    const onSubmit = (data) => {
      let payload = {
        channels: [data.channel],
        customer_name: `${data.first_name} ${data.last_name}`,
      };
      if (data.channel === 'email') {
        payload = {
          ...payload,
          email_subject: `Help "${fundraiserName}" reach their fundraising goal`,
          email_content: data.email_content,
          email: data.email,
        };
      } else {
        payload = {
          ...payload,
          sms_content: data.sms_content,
          phone_number: `${data.phone_country.dial_code}${data.phone_number}`,
        };
      }
      return dispatch(txtpayShare({ id, ...payload }));
    };

    const channelRegister = register(FIELD_NAMES.channel);
    const onChangeChannel = (e) => {
      channelRegister.onChange(e);
      clearErrors();
    };

    return (
      <form
        className={`transition-all ${className}`}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-testid="txtpay-form-container"
        ref={ref}
      >
        <p className="lg:text-lg text-base font-semibold text-javelin-gray-dark">
          {TEXT.header}
        </p>
        <p className="lg:text-sm text-xs text-javelin-gray-light mt-2">
          {TEXT.subtitle}
        </p>
        <div className="flex justify-between items-center mt-6">
          <p className="text-xs text-javelin-gray-dark">{TEXT.choose}</p>
          <div>
            <label className="mr-5">
              <input
                type="radio"
                className="border-gray-300 focus:border-javelin-blue-dark focus:ring-transparent text-javelin-blue-dark p-1 border lg:h-5 lg:w-5"
                value="email"
                ref={channelRegister.ref}
                name={channelRegister.name}
                onChange={onChangeChannel}
                data-testid="email-channel"
              />
              <span className="ml-2 text-xs text-javelin-gray-light">
                {TEXT.email}
              </span>
            </label>
            <label className="lg:mb-3 mb-1">
              <input
                type="radio"
                className="border-gray-300 focus:border-javelin-blue-dark focus:ring-transparent text-javelin-blue-dark p-1 border lg:h-5 lg:w-5"
                value="sms"
                ref={channelRegister.ref}
                name={channelRegister.name}
                onChange={onChangeChannel}
                data-testid="sms-channel"
              />
              <span className="ml-2 text-xs text-javelin-gray-light">
                {TEXT.sms}
              </span>
            </label>
          </div>
        </div>
        <hr className="horizontal-line my-4" />
        <div>
          <p className="text-xs text-javelin-gray-dark mb-3">
            {TEXT.customerDetails}
          </p>
          <FormInput
            labelClassName="pb-1 lg:pb-0 lg:w-5/12 text-xs text-javelin-gray-light flex-shrink-0"
            label={TEXT.name}
            name={FIELD_NAMES.name}
            register={register}
            error={errors[FIELD_NAMES.name]?.message}
            testId="first-name"
          />
          <FormInput
            labelClassName="pb-1 lg:pb-0 lg:w-5/12 text-xs text-javelin-gray-light flex-shrink-0"
            label={TEXT.lastName}
            name={FIELD_NAMES.last_name}
            register={register}
            error={errors[FIELD_NAMES.last_name]?.message}
            testId="last-name"
          />
          {channel === 'email' ? (
            <FormInput
              labelClassName="pb-1 lg:pb-0 lg:w-5/12 text-xs text-javelin-gray-light flex-shrink-0"
              label={TEXT.email}
              name={FIELD_NAMES.email}
              register={register}
              error={errors[FIELD_NAMES.email]?.message}
              testId="email"
            />
          ) : (
            <PhoneInput
              labelClassName="pb-1 lg:pb-0 lg:w-5/12 text-xs text-javelin-gray-light flex-shrink-0"
              inputWrapperClassName="w-full"
              className="w-full"
              name={FIELD_NAMES.phone_number}
              control={control}
              countryFieldName={FIELD_NAMES.phone_country}
              label={TEXT.phone_number}
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.phone_number]?.message}
            />
          )}
        </div>
        <hr className="horizontal-line my-4" />
        <div>
          <p className="text-xs text-javelin-gray-dark lg:mb-3 mb-1">
            {TEXT.content}
          </p>
          {channel === 'email' ? (
            <>
              <textarea
                {...register(FIELD_NAMES.email_content)}
                name={FIELD_NAMES.email_content}
                id={FIELD_NAMES.email_content}
                rows={9}
                className={`input input-full-border h-auto resize-y ${
                  !!errors[FIELD_NAMES.email_content]?.message ? 'error' : ''
                }`}
                data-testid="email_content"
              />
              {!!errors[FIELD_NAMES.email_content]?.message && (
                <ErrorMessage
                  message={errors[FIELD_NAMES.email_content].message}
                />
              )}
            </>
          ) : (
            <>
              <span />
              <textarea
                {...register(FIELD_NAMES.sms_content)}
                name={FIELD_NAMES.sms_content}
                id={FIELD_NAMES.sms_content}
                rows={3}
                className={`input input-full-border h-auto resize-none ${
                  !!errors[FIELD_NAMES.sms_content]?.message ? 'error' : ''
                }`}
                data-testid="sms_content"
              />
              {!!errors[FIELD_NAMES.sms_content]?.message && (
                <ErrorMessage
                  message={errors[FIELD_NAMES.sms_content].message}
                />
              )}
            </>
          )}
        </div>
        <Button
          title={TEXT.submit}
          className="btn-blue w-44 px-0 py-3 block ml-auto text-base mt-4"
          type="submit"
          showSpinner={isSubmitting}
        />
      </form>
    );
  }
);

TxtpayForm.propTypes = {
  id: PropTypes.string.isRequired,
  fundraiserName: PropTypes.string,
  checkoutUrl: PropTypes.string,
  className: PropTypes.string,
};
TxtpayForm.defaultProps = {
  className: 'border-t border-gray-200 p-5 lg:p-12',
};

export default TxtpayForm;
